import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import { AuthenticationService } from 'src/app/core/services/authentication/authentication.service';
import { RegisterService } from 'src/app/core/services/register/register.service';
import { InvalidHashException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { HashValidationDTO } from 'src/app/shared/models/dtos/responses/hash-validation-dto/hash-validation-dto';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  templateUrl: './invite.page.html',
})
/**
 * Business representative invitation page that instantiates a template
 */
export class InvitePage implements OnInit, OnDestroy {
  RoutesKeys = RoutesKeys;
  private subsink = new Subscription();
  errorMsg = '';
  isRepresentativeNew?: boolean;
  businessName?: string;
  hashValidationDTO!: HashValidationDTO;

  /**
   * @constructor
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private registerService: RegisterService,
    private authenticationService: AuthenticationService
  ) {}

  /**
   * Sets up title string
   */
  ngOnInit() {
    this.subsink.add(
      this.route.queryParamMap.subscribe((params) => {
        const hash = params.get('h');

        if (!hash) {
          this.router.navigate([RoutesKeys.ONBOARD]);
        } else {
          this.validateHash(hash);
        }
      })
    );
  }

  /**
   * Cleans up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Validates hash and decides whether next step is login or create account
   * @param {string} hash
   */
  async validateHash(hash: string) {
    try {
      this.hashValidationDTO = await lastValueFrom(
        this.registerService.validateHash(hash)
      );
      this.registerService.businessHashValidation = this.hashValidationDTO;
      if (!this.hashValidationDTO.valid) {
        this.navigateToExpiredInvite();
      } else {
        this.isRepresentativeNew = !this.hashValidationDTO.created;
        this.registerService.inviteNewBusiness =
          this.hashValidationDTO.invitedBusiness;
        this.registerService.inviteCurrentBusiness =
          this.hashValidationDTO.currentBusiness;
        this.registerService.invitedRepresentativeEmail =
          this.hashValidationDTO.email;
        this.authenticationService.passwordResetEmail =
          this.hashValidationDTO.email;
        this.businessName = this.hashValidationDTO.invitedBusiness.businessName;
      }
    } catch (error) {
      this.handleValidationError(error as Error);
    }
  }

  /**
   * Handles possible errors thrown in validateHash
   * @param {Error} e
   */
  handleValidationError(e: Error) {
    if (e instanceof InvalidHashException) {
      this.errorMsg = 'invalid-hash-error';
    } else {
      this.navigateToExpiredInvite();
    }
  }

  /**
   * Redirects user to login page
   */
  async redirectToLogin() {
    await this.authenticationService.logout();
    this.router.navigate([RoutesKeys.LOGIN], {
      queryParams: {
        redirectTo: RoutesKeys.INVITE_EXISTING,
      },
    });
  }

  /**
   * Redirects user to create account page
   */
  redirectToCreateAccountPage() {
    this.registerService.sendHashValidationDTO(this.hashValidationDTO);
    this.router.navigate([RoutesKeys.CREATE_ACCOUNT]);
  }

  /**
   * Navigates to expired invite page
   */
  private navigateToExpiredInvite() {
    this.registerService.routingGuards.expiredInvite = true;
    this.router.navigate([RoutesKeys.EXPIRED_INVITE]);
  }
}
